import React from "react";

/**
 * UserCard - Vertical card component for displaying user profiles
 * Optimized for grid layouts on both mobile and desktop
 *
 * @param {string} username - User's username
 * @param {string} image - User's profile picture URL
 * @param {boolean} online - Whether the user is online
 * @param {string} subtitle - Additional info (e.g., current song or last seen)
 * @param {function} onSelect - Handler for card click
 * @param {ReactNode} action - Optional action button (e.g., view profile)
 */
export default function UserCard({
  username,
  image,
  online,
  subtitle,
  onSelect,
  action,
}) {
  return (
    <div className="group relative bg-secondary border border-neutral-800 rounded-md overflow-hidden transition-all duration-200 ease-out hover:border-neutral-700 hover:shadow-lg cursor-pointer animate-scale-in">
      {/* Main clickable area */}
      <div onClick={onSelect}>
        {/* Avatar Container */}
        <div className="relative p-4 md:p-6">
          <div className="relative mx-auto w-20 h-20 md:w-24 md:h-24">
            {/* Avatar Image */}
            <img
              src={image}
              alt={username}
              className="w-full h-full rounded-full object-cover border-2 border-neutral-700 shadow-md"
            />

            {/* Online Status Indicator */}
            {online !== undefined && (
              <div className="absolute bottom-0 right-0 w-5 h-5 md:w-6 md:h-6 rounded-full border border-neutral-700 shadow-md"
                style={{ backgroundColor: online ? "#0dd15e" : "#AAA" }}
              />
            )}

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-full transition-all duration-300" />
          </div>
        </div>

        {/* Content */}
        <div className="px-3 pb-3 md:px-4 md:pb-4 text-center">
          <h3 className="text-sm md:text-base font-semibold text-text truncate mb-1">
            {username}
          </h3>
          {subtitle && (
            <p className="text-xs md:text-sm text-text-muted truncate">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Action button (e.g., view profile) - Shows on hover */}
      {action && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <div className="bg-black bg-opacity-70 hover:bg-opacity-90 rounded-full p-2 backdrop-blur-sm">
            {action}
          </div>
        </div>
      )}
    </div>
  );
}
